import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';

/**
 * Fetches the approval rules for a specific condominium.
 * @param {string} condominioId 
 * @returns {Promise<object|null>} The approval rules object or null if not found/disabled.
 */
export const getApprovalRules = async (condominioId) => {
    try {
        const docRef = doc(db, "condominios", condominioId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.approvalRules && data.approvalRules.enabled) {
                return data.approvalRules;
            }
        }
        return null;
    } catch (error) {
        console.error("Error fetching approval rules:", error);
        return null;
    }
};

/**
 * Determines the status of an invoice based on its amount and the condominium's rules.
 * Supports sequential approval (Cascading).
 * For payroll (isPayroll = true), it ignores the amount threshold and forces approval through all configured levels.
 * @param {number} amount - The total amount of the invoice.
 * @param {object} rules - The approval rules object (from getApprovalRules).
 * @param {number} [lastApprovedLevel=0] - The last level that was approved (default 0).
 * @param {boolean} [isPayroll=false] - Whether this is a payroll receipt.
 * @returns {object} { status: 'approved' | 'pending_approval', level: number | null, requiredApprovers: string[] }
 */
export const validateInvoiceRules = (amount, rules, lastApprovedLevel = 0, isPayroll = false) => {
    // Default result: Approved (no approval needed)
    let result = {
        status: 'approved',
        nextStatus: 'pendiente',
        requiresApproval: false,
        approvalLevel: null,
        approvers: []
    };

    if (!rules || !rules.levels || rules.levels.length === 0) {
        return result;
    }

    // Sort levels by threshold ASCENDING for sequential check.
    // Level 1 (Low) -> Level 2 (High)
    const sortedLevels = [...rules.levels].sort((a, b) => a.threshold - b.threshold);

    for (const level of sortedLevels) {
        // Only check levels higher than what we've already passed
        const currentLevelId = parseInt(level.levelId) || 0;

        // If it's payroll, we ignore the threshold and force it through the level
        const qualifiesForLevel = isPayroll || amount >= parseFloat(level.threshold);

        if (qualifiesForLevel) {
            if (currentLevelId > lastApprovedLevel) {
                // Represents the next step in the chain
                return {
                    status: 'pending_approval',
                    nextStatus: 'por_aprobar',
                    requiresApproval: true,
                    approvalLevel: currentLevelId,
                    approvers: level.approvers || []
                };
            }
        }
    }
    // If no higher level triggers, we are done.
    return result;
};
